import React, { useState, useEffect, useRef } from 'react';
import './ThreeDPreview.css';
import ThreeDController from '../utils/ThreeDController';
import * as THREE from 'three';

const ThreeDPreview = ({ product, baseUrl, is3DProduct, selectedView, selectedViewIndex, konvaController, externalShow }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const canvasRef = useRef(null);
  const controllerRef = useRef(null);
  const lastVariantTextureUrlRef = useRef(null);
  const pendingVariantRef = useRef(null);
  const modelHasDesignMeshRef = useRef(false);
  const hasVariantBaseTextureRef = useRef(false);
  const didAutoOpenRef = useRef(false);
  const toggleGestureRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    moved: false
  });
  const syncTimerRef = useRef(null);
  const syncIntervalRef = useRef(null);
  const syncInFlightRef = useRef(false);
  const pendingSyncRef = useRef(false);
  const lastSyncAtRef = useRef(0);
  const lastTextureRef = useRef(null);
  const lastDesignDataUrlRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const [currentViewIndex, setCurrentViewIndex] = useState(selectedViewIndex);
  const [textureApplied, setTextureApplied] = useState(false);

  const isImageBitmapFullyTransparent = (imageBitmap) => {
    if (!imageBitmap) return false;
    try {
      const w = imageBitmap.width || 0;
      const h = imageBitmap.height || 0;
      if (w <= 0 || h <= 0) return false;

      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(imageBitmap, 0, 0);
      const data = ctx.getImageData(0, 0, w, h).data;

      for (let i = 3; i < data.length; i += 4) {
        if (data[i] !== 0) return false;
      }
      return true;
    } catch (e) {
      console.warn('[3D] Failed to check design transparency:', e);
      return false;
    }
  };

  const makeFullyTransparentTexture = () => {
    const c = document.createElement('canvas');
    c.width = 1;
    c.height = 1;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, 1, 1);
    const t = new THREE.CanvasTexture(c);
    try {
      t.colorSpace = THREE.SRGBColorSpace;
    } catch {}
    t.needsUpdate = true;
    return t;
  };
  
  const togglePreview = () => {
    setShowPreview(prevState => !prevState);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(v => !v);
  };

  const onPreviewPointerDown = (e) => {
    try {
      toggleGestureRef.current.active = true;
      toggleGestureRef.current.moved = false;
      toggleGestureRef.current.startX = e.clientX;
      toggleGestureRef.current.startY = e.clientY;
    } catch {}
  };

  const onPreviewPointerMove = (e) => {
    try {
      if (!toggleGestureRef.current.active) return;
      const dx = Math.abs(e.clientX - toggleGestureRef.current.startX);
      const dy = Math.abs(e.clientY - toggleGestureRef.current.startY);
      if (dx > 6 || dy > 6) {
        toggleGestureRef.current.moved = true;
      }
    } catch {}
  };

  const onPreviewPointerUp = () => {
    try {
      if (!toggleGestureRef.current.active) return;
      const shouldToggle = !toggleGestureRef.current.moved;
      toggleGestureRef.current.active = false;
      if (shouldToggle) {
        toggleFullscreen();
      }
    } catch {}
  };

  const onPreviewPointerCancel = () => {
    toggleGestureRef.current.active = false;
    toggleGestureRef.current.moved = false;
  };

  // Sync internal visibility with external controller when provided
  useEffect(() => {
    if (typeof externalShow === 'boolean') {
      setShowPreview(externalShow);
    }
  }, [externalShow]);

  // Default open for 3D products (once), unless externalShow is explicitly controlling it
  useEffect(() => {
    if (!is3DProduct) return;
    if (!product) return;
    if (typeof externalShow === 'boolean') return;
    if (didAutoOpenRef.current) return;

    didAutoOpenRef.current = true;
    setShowPreview(true);
  }, [is3DProduct, product, externalShow]);

  // Add effect to close 3D preview when view changes
  useEffect(() => {
    // When the user changes the view, close the 3D preview if it is open
    if (showPreview) {
      setShowPreview(false);
      setTextureApplied(false);
      setIsFullscreen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedView, selectedViewIndex]);

  // Esc to exit fullscreen
  useEffect(() => {
    if (!showPreview) return;
    if (!isFullscreen) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsFullscreen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [showPreview, isFullscreen]);

  // Apply base (variant) 3D texture when the active variant changes
  useEffect(() => {
    if (!showPreview) return;
    if (!controllerRef.current) return;

    const applyVariantBaseTexture = async (variant) => {
      try {
        pendingVariantRef.current = variant || null;
        const url = variant && variant['3Dtexture'] ? String(variant['3Dtexture']).trim() : '';

        let baseTargetMeshName = null;
        try {
          const c = controllerRef.current;
          if (c && c.model && typeof c.hasMeshNameContainsInsensitive === 'function') {
            if (c.hasMeshNameContainsInsensitive(c.model, 'Product')) {
              baseTargetMeshName = 'Product';
            }
          }
        } catch {}

        // If the model isn't loaded yet, just record the pending variant and bail.
        if (!controllerRef.current || !controllerRef.current.model) {
          return;
        }

        if (!url) {
          hasVariantBaseTextureRef.current = false;
          if (lastVariantTextureUrlRef.current) {
            lastVariantTextureUrlRef.current = null;
          }
          // Debug default: show Product mesh in yellow when no variant 3Dtexture is present
          if (typeof controllerRef.current.applySolidColorMaterial === 'function') {
            if (baseTargetMeshName) {
              controllerRef.current.applySolidColorMaterial(0xffff00, baseTargetMeshName);
            } else {
              controllerRef.current.applySolidColorMaterial(0xffff00, null, { excludeMeshName: 'Design', excludeMeshMatch: 'contains' });
            }
          } else if (typeof controllerRef.current.applyBaseWhiteMaterial === 'function') {
            controllerRef.current.applyBaseWhiteMaterial();
          }
          return;
        }

        hasVariantBaseTextureRef.current = true;

        if (lastVariantTextureUrlRef.current === url) return;
        lastVariantTextureUrlRef.current = url;

        await controllerRef.current.loadTexture(url, {
          repeat: { x: 1, y: 1 },
          offset: { x: 0, y: 0 },
          ...(baseTargetMeshName
            ? { targetMeshName: baseTargetMeshName, targetMeshMatch: 'contains' }
            : { excludeMeshName: 'Design', excludeMeshMatch: 'contains' })
        }, false);
      } catch (e) {
        console.warn('[ThreeDPreview] Failed to apply variant base 3Dtexture; reverting to white', e);
        try {
          lastVariantTextureUrlRef.current = null;
          hasVariantBaseTextureRef.current = false;
          if (typeof controllerRef.current.applySolidColorMaterial === 'function') {
            controllerRef.current.applySolidColorMaterial(0xffff00, null, { excludeMeshName: 'Design', excludeMeshMatch: 'contains' });
          } else if (typeof controllerRef.current.applyBaseWhiteMaterial === 'function') {
            controllerRef.current.applyBaseWhiteMaterial();
          }
        } catch {}
      }
    };

    const onActiveVariantChanged = (e) => {
      const v = e?.detail?.variant || null;
      applyVariantBaseTexture(v);
    };

    window.addEventListener('activeVariantChanged', onActiveVariantChanged);

    // Apply immediately if already available
    try {
      if (typeof window !== 'undefined' && window.activeVariant) {
        applyVariantBaseTexture(window.activeVariant);
      }
    } catch {}

    return () => {
      window.removeEventListener('activeVariantChanged', onActiveVariantChanged);
    };
  }, [showPreview]);
  
  /**
   * Apply the current design as a texture to the 3D model
   * @param {string} designImageUrl - Optional URL to design image, if not provided will use konvaController to export
   */
  const applyDesignTexture = async (designImageUrl) => {
    if (!controllerRef.current || !showPreview) {
      console.warn('Cannot apply texture: 3D preview not active');
      return;
    }

    // If the model has no dedicated Design mesh and we have a variant base texture,
    // do not overwrite the model's base texture with the live design texture.
    if (!modelHasDesignMeshRef.current && hasVariantBaseTextureRef.current) {
      return;
    }

    try {
      // eslint-disable-next-line no-unused-vars
      let textureUrl = designImageUrl;
      // eslint-disable-next-line no-unused-vars
      let textureSource = 'provided';
      
      // Export texture layer for 3D model (strict requirement)
      const designLayer = konvaController.layers && konvaController.layers['Design'];
      const designGroup = konvaController.design_group;
      if (!konvaController || typeof konvaController.exportTextureLayer !== 'function' || !designLayer || !designGroup) {
        console.error('[ThreeDPreview] exportTextureLayer unavailable or missing design context');
        setTextureApplied(false);
        throw new Error('exportTextureLayer unavailable');
      }
      let preview_image;
      try {
        preview_image = await konvaController.exportTextureLayer(designLayer, designGroup, { includeCase: true });
      } catch (err) {
        console.error('[ThreeDPreview] exportTextureLayer failed:', err);
        setTextureApplied(false);
        throw err;
      }

      // If the exported data URL did not change, do not reapply/dispose textures.
      // This avoids visible flashing when the sync loop runs frequently.
      if (preview_image && lastDesignDataUrlRef.current === preview_image) {
        return;
      }
      lastDesignDataUrlRef.current = preview_image;
      
      // Step 2: Convert base64 to blob
      const blob = await fetch(preview_image).then(res => res.blob());
      
      // Step 3: Create a texture from the blob with proper settings
      const texture = new THREE.Texture();
      const imageBitmap = await createImageBitmap(blob, {
        imageOrientation: 'flipY', // Ensure correct orientation
        premultiplyAlpha: 'none', // Prevent black fill when canvas is transparent
        colorSpaceConversion: 'default' // Use default color space
      });
      
      // Log texture dimensions for debugging
      void imageBitmap;

      const isEmptyDesign = isImageBitmapFullyTransparent(imageBitmap);
      if (isEmptyDesign) {
        const emptyTex = makeFullyTransparentTexture();

        try {
          if (lastTextureRef.current) {
            lastTextureRef.current.dispose();
            lastTextureRef.current = null;
          }
        } catch {}

        const enableAlpha = true;

        controllerRef.current.applyTextureToModel(emptyTex, {
          repeat: { x: 1, y: 1 },
          offset: { x: 0, y: 0 },
          enableAlpha,
          targetMeshName: 'Design',
          targetMeshMatch: 'contains'
        });

        lastTextureRef.current = emptyTex;
        setTextureApplied(false);
        return;
      }
      
      texture.image = imageBitmap;
      texture.needsUpdate = true;

      try {
        texture.colorSpace = THREE.SRGBColorSpace;
      } catch {}
      
      // Set proper texture parameters for better quality
      texture.wrapS = THREE.ClampToEdgeWrapping;
      texture.wrapT = THREE.ClampToEdgeWrapping;
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      
      // Step 4: Apply the texture to the 3D model
      // Dispose previous texture to avoid leaking GPU memory
      try {
        if (lastTextureRef.current) {
          lastTextureRef.current.dispose();
          lastTextureRef.current = null;
        }
      } catch {}

      let targetMeshName = null;
      try {
        const c = controllerRef.current;
        if (c && c.model && typeof c.hasMeshNameContainsInsensitive === 'function') {
          if (c.hasMeshNameContainsInsensitive(c.model, 'Design')) {
            targetMeshName = 'Design';
          }
        }
      } catch {}

      let enableAlpha = false;
      try {
        const viewId = typeof selectedView === 'object' ? selectedView.id : selectedView;
        const commonViews = product?.CommonProperties?.Views;
        if (Array.isArray(commonViews) && viewId) {
          const match = commonViews.find(v => {
            const id = typeof v === 'object' ? v.id : v;
            return id && String(id).toUpperCase() === String(viewId).toUpperCase();
          });
          if (match && typeof match === 'object' && typeof match['3DenableAlpha'] === 'boolean') {
            enableAlpha = match['3DenableAlpha'];
          }
        }
      } catch {}

      // Design mesh should behave like air/glass: always respect alpha.
      enableAlpha = true;

      controllerRef.current.applyTextureToModel(texture, {
        repeat: { x: 1, y: 1 },
        offset: { x: 0, y: 0 },
        enableAlpha,
        ...(targetMeshName ? { targetMeshName, targetMeshMatch: 'contains' } : { targetMeshName: 'Design', targetMeshMatch: 'contains' })
      });

      lastTextureRef.current = texture;
      
      setTextureApplied(true);
    } catch (error) {
      console.error('Error applying design texture:', error);
    }
  };

  // Debounced live sync from 2D->3D
  const requestLiveSync = (reason = 'change', delayMs = 350) => {
    if (!showPreview) return;
    if (!konvaController) return;
    if (!controllerRef.current) return;

    pendingSyncRef.current = true;
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(async () => {
      if (!pendingSyncRef.current) return;
      if (syncInFlightRef.current) return;

      const now = Date.now();
      // Basic throttle to avoid hammering exports
      if (now - lastSyncAtRef.current < 100) {
        return;
      }

      pendingSyncRef.current = false;
      syncInFlightRef.current = true;
      lastSyncAtRef.current = now;

      try {
        await applyDesignTexture();
      } catch (e) {
        console.warn('[ThreeDPreview] Live sync failed:', e);
      } finally {
        syncInFlightRef.current = false;
        // If changes came in during the export, schedule another update
        if (pendingSyncRef.current) {
          requestLiveSync('pending', 250);
        }
      }
    }, delayMs);
  };
  
  // Effect for initializing the 3D controller when preview is toggled on
  useEffect(() => {
    if (showPreview && canvasRef.current && product) {
      // Get the canvas dimensions
      const container = canvasRef.current;
      const { clientWidth, clientHeight } = container;
      
      // Initialize the controller if it doesn't exist
      if (!controllerRef.current) {
        controllerRef.current = new ThreeDController(container);
        controllerRef.current.init({
          width: clientWidth || 300,
          height: clientHeight || 300,
          backgroundColor: '#f2f2f2',
          autoRotate: false
        });

        // Console helpers: hide/show Design and Product meshes with logs
        window.hideDesignMeshes = (hide = true) => {
          try {
            const c = controllerRef.current;
            if (!c || !c.model) {
              console.error('[hideDesignMeshes] controller/model not ready');
              return;
            }

            let count = 0;
            c.model.traverse((child) => {
              if (!child || !child.isMesh) return;
              const name = String(child.name || 'unnamed');
              const lower = name.toLowerCase();
              if (!lower.includes('design')) return;
              child.visible = !hide;
              count += 1;
              console.log(`[hideDesignMeshes] ${hide ? 'HIDE' : 'SHOW'}: ${name}`);
            });

            console.log(`[hideDesignMeshes] done. matched meshes: ${count}`);
            try { c.renderer?.render(c.scene, c.camera); } catch {}
          } catch (e) {
            console.error('[hideDesignMeshes] error', e);
          }
        };

        window.hideProductMeshes = (hide = true) => {
          try {
            const c = controllerRef.current;
            if (!c || !c.model) {
              console.error('[hideProductMeshes] controller/model not ready');
              return;
            }

            let count = 0;
            c.model.traverse((child) => {
              if (!child || !child.isMesh) return;
              const name = String(child.name || 'unnamed');
              const lower = name.toLowerCase();
              // Treat anything with "product" in name as product mesh, but avoid design meshes
              if (!lower.includes('product') || lower.includes('design')) return;
              child.visible = !hide;
              count += 1;
              console.log(`[hideProductMeshes] ${hide ? 'HIDE' : 'SHOW'}: ${name}`);
            });

            console.log(`[hideProductMeshes] done. matched meshes: ${count}`);
            try { c.renderer?.render(c.scene, c.camera); } catch {}
          } catch (e) {
            console.error('[hideProductMeshes] error', e);
          }
        };
      }
      
      // Handle window resize
      const handleResize = () => {
        if (controllerRef.current && container) {
          controllerRef.current.resize(container.clientWidth, container.clientHeight);
        }
      };
      
      window.addEventListener('resize', handleResize);
      
      // Clean up
      return () => {
        window.removeEventListener('resize', handleResize);
        setIsFullscreen(false);
        if (syncTimerRef.current) {
          clearTimeout(syncTimerRef.current);
          syncTimerRef.current = null;
        }
        if (syncIntervalRef.current) {
          clearInterval(syncIntervalRef.current);
          syncIntervalRef.current = null;
        }
        try {
          if (lastTextureRef.current) {
            lastTextureRef.current.dispose();
            lastTextureRef.current = null;
          }
        } catch {}
        if (controllerRef.current) {
          controllerRef.current.dispose();
          controllerRef.current = null;
        }
      };
    }
  }, [showPreview, product]);

  // Resize renderer when fullscreen toggles
  useEffect(() => {
    if (!showPreview) return;
    const container = canvasRef.current;
    if (!container) return;
    if (!controllerRef.current) return;

    const id = window.setTimeout(() => {
      try {
        controllerRef.current.resize(container.clientWidth, container.clientHeight);
      } catch {}
    }, 0);

    return () => {
      window.clearTimeout(id);
    };
  }, [showPreview, isFullscreen]);

  // Fixed interval sync while 3D preview is visible (0.5s)
  useEffect(() => {
    if (!showPreview) return;
    if (!konvaController) return;

    // Kick one immediate sync when opening
    requestLiveSync('timer-open', 0);

    if (syncIntervalRef.current) {
      clearInterval(syncIntervalRef.current);
      syncIntervalRef.current = null;
    }

    syncIntervalRef.current = setInterval(() => {
      requestLiveSync('timer', 250);
    }, 800);

    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
        syncIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPreview, konvaController]);

  // Subscribe to Konva events for live syncing
  useEffect(() => {
    if (!showPreview) return;
    if (!konvaController || !konvaController.stage) return;

    const stage = konvaController.stage;
    const onDragMove = () => requestLiveSync('dragmove', 250);
    const onTransform = () => requestLiveSync('transform', 250);
    const onDragEnd = () => requestLiveSync('dragend', 120);
    const onTransformEnd = () => requestLiveSync('transformend', 120);
    const onMouseUp = () => requestLiveSync('pointerup', 180);

    // Attribute changes (e.g., TextTab editing text/color/font/shadow/etc.)
    const onAttrChange = () => requestLiveSync('attrChange', 250);
    const attrEvents = [
      'textChange',
      'fontSizeChange',
      'fontFamilyChange',
      'fontStyleChange',
      'fontVariantChange',
      'textDecorationChange',
      'alignChange',
      'lineHeightChange',
      'letterSpacingChange',
      'fillChange',
      'opacityChange',
      'strokeChange',
      'strokeWidthChange',
      'shadowColorChange',
      'shadowBlurChange',
      'shadowOffsetXChange',
      'shadowOffsetYChange',
      'shadowOpacityChange',
      'scaleXChange',
      'scaleYChange',
      'rotationChange',
      'skewXChange',
      'skewYChange',
      'widthChange',
      'heightChange'
    ].join(' ');

    // Konva events bubble to stage
    stage.on('dragmove', onDragMove);
    stage.on('transform', onTransform);
    stage.on('dragend', onDragEnd);
    stage.on('transformend', onTransformEnd);
    stage.on('mouseup touchend', onMouseUp);
    stage.on(attrEvents, onAttrChange);

    return () => {
      try {
        stage.off('dragmove', onDragMove);
        stage.off('transform', onTransform);
        stage.off('dragend', onDragEnd);
        stage.off('transformend', onTransformEnd);
        stage.off('mouseup touchend', onMouseUp);
        stage.off(attrEvents, onAttrChange);
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPreview, konvaController]);
  
  // Separate effect to load the model when the selected view changes
  useEffect(() => {
    // Only proceed if the controller exists and preview is shown
    if (controllerRef.current && showPreview && product) {
      setCurrentViewIndex(selectedViewIndex);
      setTextureApplied(false); // Reset texture applied state when view changes
      
      // Get the model URL from the product's 3DMODEL array if available
      let modelUrl = 'placeholder';
      
      // Get the current view ID - handle both string and object formats
      const viewId = typeof selectedView === 'object' ? selectedView.id : selectedView;
      
      if (product['3DMODEL'] && Array.isArray(product['3DMODEL']) && product['3DMODEL'].length > 0) {
        
        // Get the product views array (handle both uppercase and lowercase property names)
        const productViews = product.Views || product.views || [];
        
        // Find the index of the current view in the product views array
        let viewIndex = -1;
        
        // Search for the view in the product views array
        for (let i = 0; i < productViews.length; i++) {
          const currentView = productViews[i];
          const currentViewId = typeof currentView === 'object' ? currentView.id : currentView;
          
          if (currentViewId && currentViewId.toUpperCase() === viewId.toUpperCase()) {
            viewIndex = i;
            break;
          }
        }
        
        
        // Use the found view index to get the correct model
        if (viewIndex >= 0 && viewIndex < product['3DMODEL'].length) {
          // Add timestamp to prevent caching
          const timestamp = new Date().getTime();
          modelUrl = `${product['3DMODEL'][viewIndex]}?t=${timestamp}`;
        } else {
          // Add timestamp to prevent caching
          const timestamp = new Date().getTime();
          modelUrl = `${product['3DMODEL'][0]}?t=${timestamp}`; // Default to first model if view index is out of bounds
        }
      } else {
        console.warn('No 3DMODEL array found in product data or array is empty');
      }
      
      // Load the model
      try {
        // Create a promise to track when model loading is complete
        const loadModelPromise = new Promise((resolve) => {
          // Add a one-time event listener for the model loaded event
          const handleModelLoaded = () => {
            try {
              if (controllerRef.current && typeof controllerRef.current.applySolidColorMaterial === 'function') {
                controllerRef.current.applySolidColorMaterial(0xf2f2f2, null, { excludeMeshName: 'Design', excludeMeshMatch: 'contains' });
              }
            } catch {}
            resolve();
          };
          
          // Load the model
          controllerRef.current.loadModel(modelUrl, { onLoaded: handleModelLoaded, materialType: selectedView?.['3DMaterial'], baseColor: selectedView?.['3DBaseColor'] });
        });
        
        // After model is loaded, automatically apply texture
        loadModelPromise.then(() => {
          // Apply texture after a short delay to ensure model is fully initialized
          setTimeout(() => {
            try {
              try {
                const c = controllerRef.current;
                modelHasDesignMeshRef.current = !!(c && c.model && typeof c.hasMeshNameContainsInsensitive === 'function' && c.hasMeshNameContainsInsensitive(c.model, 'Design'));
              } catch {
                modelHasDesignMeshRef.current = false;
              }

              let baseTargetMeshName = null;
              try {
                const c = controllerRef.current;
                if (c && c.model && typeof c.hasMeshNameContainsInsensitive === 'function') {
                  if (c.hasMeshNameContainsInsensitive(c.model, 'Product')) {
                    baseTargetMeshName = 'Product';
                  }
                }
              } catch {}

              const v = pendingVariantRef.current || (typeof window !== 'undefined' ? window.activeVariant : null);
              if (controllerRef.current) {
                const url = v && v['3Dtexture'] ? String(v['3Dtexture']).trim() : '';
                if (!url) {
                  hasVariantBaseTextureRef.current = false;
                  // Debug default: show Product mesh in yellow when no variant 3Dtexture is present
                  if (typeof controllerRef.current.applySolidColorMaterial === 'function') {
                    if (baseTargetMeshName) {
                      controllerRef.current.applySolidColorMaterial(0xf2f2f2, baseTargetMeshName);
                    } else {
                      controllerRef.current.applySolidColorMaterial(0xf2f2f2, null, { excludeMeshName: 'Design', excludeMeshMatch: 'contains' });
                    }
                  } else if (typeof controllerRef.current.applyBaseWhiteMaterial === 'function') {
                    controllerRef.current.applyBaseWhiteMaterial();
                  }
                } else if (typeof controllerRef.current.loadTexture === 'function') {
                  hasVariantBaseTextureRef.current = true;
                  controllerRef.current.loadTexture(url, {
                    repeat: { x: 1, y: 1 },
                    offset: { x: 0, y: 0 },
                    ...(baseTargetMeshName
                      ? { targetMeshName: baseTargetMeshName, targetMeshMatch: 'contains' }
                      : { excludeMeshName: 'Design', excludeMeshMatch: 'contains' })
                  }, false);
                }
              }
            } catch {}
            // Only auto-apply the design texture when it won't overwrite the variant base texture.
            // If there's no Design mesh and a variant base texture is present, the design texture would replace it.
            if (modelHasDesignMeshRef.current || !hasVariantBaseTextureRef.current) {
              applyDesignTexture();
            }
          }, 500);
        });
      } catch (error) {
        console.error('Failed to load 3D model:', error);
      }
    }
  }, [selectedView, selectedViewIndex, showPreview, product, konvaController]);
  
  // Only show the 3D preview button if is3DProduct is true
  if (!is3DProduct) {
    return null;
  }
  
  return (
    <>
      {showPreview && (
        <div
          className={`three-d-preview-content${isFullscreen ? ' fullscreen' : ''}`}
          onPointerDown={onPreviewPointerDown}
          onPointerMove={onPreviewPointerMove}
          onPointerUp={onPreviewPointerUp}
          onPointerCancel={onPreviewPointerCancel}
          role="button"
          tabIndex={0}
        >
          <div id="three-d-canvas" ref={canvasRef}>
            {/* Three.js will render here */}
          </div>
        </div>
      )}
    </>
  );
};

export default ThreeDPreview;
